import * as THREE from "three";

/**
 * The foot IK's legs and tunables (footIk.ts solves them, footPlace.ts places each, footPin.ts holds the planted ones).
 *
 * Feet on the ground they stand over (groundFollow.ts places the body; this places the soles). The HQ rigs are rigid
 * parts (rigidRig.ts): a biped's leg is one `boot` part hung from the hip, a four-legged rig's leg is two — `boot` /
 * `arm` from the hip or shoulder and `shin` / `fore` from the knee. After the clip has posed them:
 *
 * - each sole is measured where the clip put it, with how high the clip lifted it (a swinging foot) above its rest;
 * - the pelvis drops or rises so the planted soles (the ones the clip holds down) reach the ground under them, but never
 *   so far that another sole would have to go further into the bank than its leg can shorten;
 * - each leg then makes up the rest: a two-part leg by bending or straightening at the knee (two-bone IK, the sole kept
 *   on its line from the hip), a one-part leg by shortening or lengthening about the hip, within limits.
 *
 * The leg's own parts stay the clip's: an identity group is slipped above each (`#ik`), and only those groups move, so
 * the mixer never sees (or fights) the correction. Only units on uneven ground run it; nothing here allocates per frame.
 */
export const FOOT_IK = {
  /** The ground under the footprint is uneven by more than this (world units): the feet are placed. */
  unevenAbove: 0.006,
  /** A sole lifted this share of its leg's length above its rest by the clip is half planted (a weight of ½). */
  plantedBelow: 0.1,
  /**
   * A one-part leg may shorten or lengthen by these shares of its length; a swinging one (not planted) may tuck up by
   * `tuck`, so a foot stepping up a ramp clears it without lifting the body off the planted one.
   */
  shorten: 0.55, lengthen: 0.6, tuck: 0.8,
  /** A two-part leg reaches from this share of its full length (knee bent) to this one (nearly straight). */
  reachMin: 0.4, reachMax: 0.985,
  /** The pelvis moves at most this share of the longest leg, eased at this rate (rad/s, critically damped). */
  pelvisMax: 0.6, pelvisOmega: 28,
  /**
   * The lock pins a sole while the clip holds it in the lowest `pinShare` of its own step (between the lowest and the
   * highest the clip has lately lifted it: `low` and `high` creep together at `bandCreep` leg lengths a second, so they
   * follow its stance and swing a step at a time; a step under `minStep` of the leg counts as that), and lets it go
   * as the clip lifts it to `releaseShare` of its step (a sprawler's 2 cm swing and a boss's 12 cm one alike: a fixed
   * share of the leg pinned the one's swinging feet and let the other's rolling planted ones go), or once the clip
   * has carried it `releaseAway` of its leg's length from the pin (the pin is out of a sane reach).
   */
  pinShare: 0.25, releaseShare: 0.5, bandCreep: 0.03, minStep: 0.02, releaseAway: 0.5,
  /**
   * A lock takes hold at once (a pin is made where the sole already is, so nothing jumps); dropped, it holds `unlockHold`
   * s (a walking clip lifts every planted foot by then, and a lifted foot lets its pin go over the lift band below), then
   * eases back to the clip at `lockRate` (1/s). A pin's hold fades as the clip lifts the sole from `pinShare` to
   * `releaseShare` of its step, and as it carries it from `awayFade` x `releaseAway` to `releaseAway` from the pin, so a
   * planted sole is never blended across the ground.
   */
  lockRate: 14, unlockHold: 0.5, awayFade: 0.5,
  /**
   * A leg whose sole stands out to the side of its hip by more than this share of the hip's height (the salamander's)
   * is never pinned: its clip sweeps it round its hip, and a pin held it where a sprawled leg could not reach as the
   * body walked on, so the drawn foot slid (0.20 of the walk where its clip planted to 0.07, slip6 probe).
   */
  sprawlAbove: 0.8,
  /**
   * ...and on ground within this share of its leg's length of where its clip puts the sole it is not placed at all:
   * a sprawled leg's two-bone reach could only land the sole by swinging it across the ground (its height correction
   * slid the salamander's feet at 0.14 of the walk where its clip planted to 0.09). Nor does it ever count for the
   * pelvis, which one placed sprawler alone dragged 7 cm down under the rest.
   */
  sprawlSlack: 0.5,
} as const;

export interface Leg {
  readonly upper: THREE.Object3D;
  readonly lower: THREE.Object3D | null;
  readonly g1: THREE.Group;
  readonly g2: THREE.Group | null;
  /** The sole in the last part's own space, and the leg's rest lengths (model units): hip height, or the two bones. */
  readonly sole: THREE.Vector3;
  readonly len: number; readonly l1: number; readonly l2: number;
  /** This frame (world): how far the sole must move up to meet its ground, how planted it is, its stretch limits. */
  need: number; weight: number; shortenBy: number; lengthenBy: number;
  /** This frame: the lift the clip gives the sole (world units) and where the sole goes across the ground (x, z). */
  lift: number; tx: number; tz: number;
  /** Held by a lock (`lock`): the sole is pinned at (px, pz) until the clip lifts it. */
  pinned: boolean; px: number; pz: number;
  /** The lowest and highest lift the clip has lately given the sole (world units, creeping together): its step. */
  low: number; high: number;
  /** It sprawls out to the side (a salamander's): the lock leaves it to its clip (FOOT_IK.sprawlAbove). */
  readonly sprawl: boolean;
  /** This frame it is left to its clip altogether (a sprawler on gentle ground: FOOT_IK.sprawlSlack). */
  free: boolean;
}

const IK_SUFFIX = "#ik";

/**
 * The legs of `model` under `body` (its `boot_l/_r` with a `shin`, and `arm_l/_r` only when they carry a `fore`),
 * each with an `#ik` group slipped in above its parts. Run on the rest pose.
 */
export function attachLegs(body: THREE.Object3D, model: string): Leg[] {
  const legs: Leg[] = [];
  for (const [top, low] of LEG_PARTS) {
    for (const side of SIDES) {
      const upper = part(body, model, `${top}_${side}`);
      if (!upper || !upper.parent) continue;
      const lower = part(upper, model, `${low}_${side}`);
      if (top === "arm" && !lower) continue;                 // a biped's arm is not a leg
      legs.push(makeLeg(upper, lower, body));
    }
  }
  return legs;
}

function makeLeg(upper: THREE.Object3D, lower: THREE.Object3D | null, body: THREE.Object3D): Leg {
  // rest pose, in the space of the leg's parent (the body part, standing on y = 0): the sole sits under the last pivot
  const mu = new THREE.Matrix4().compose(upper.position, upper.quaternion, upper.scale);
  const last = lower ? mu.clone().multiply(new THREE.Matrix4().compose(lower.position, lower.quaternion, lower.scale)) : mu;
  const pivot = new THREE.Vector3().setFromMatrixPosition(last), hip = new THREE.Vector3().setFromMatrixPosition(mu);
  const soleRest = new THREE.Vector3(pivot.x, 0, pivot.z);
  const sole = drawnSole(body, lower ?? upper) ?? soleRest.clone().applyMatrix4(last.clone().invert());
  const g1 = slipGroup(upper), g2 = lower ? slipGroup(lower) : null;
  const at = sole.clone().applyMatrix4(last), sprawl = Math.abs(at.x - hip.x) > FOOT_IK.sprawlAbove * Math.max(1e-6, hip.y - at.y);
  return {
    upper, lower, g1, g2, sole, sprawl, free: false,
    len: Math.max(1e-3, hip.y), l1: hip.distanceTo(pivot), l2: pivot.distanceTo(soleRest),
    need: 0, weight: 0, shortenBy: 0, lengthenBy: 0, lift: 0, tx: 0, tz: 0, pinned: false, px: 0, pz: 0, low: 0, high: 0,
  };
}

/** An identity group between a part and its parent, in the part's place among its siblings. */
function slipGroup(part: THREE.Object3D): THREE.Group {
  const parent = part.parent!;
  const g = new THREE.Group();
  g.name = `${part.name}${IK_SUFFIX}`;
  const at = parent.children.indexOf(part);
  parent.remove(part);
  parent.children.splice(at, 0, g);
  g.parent = parent;
  g.add(part);
  return g;
}

/** A part of `model` under `root`: the loader drops the dot from `model.part` (hqNode in common.ts reads it the same way). */
function part(root: THREE.Object3D, model: string, name: string): THREE.Object3D | null {
  return root.getObjectByName(`${model}${name}`) ?? root.getObjectByName(`${model}.${name}`) ?? null;
}

const LEG_PARTS: readonly (readonly [string, string])[] = [["boot", "shin"], ["arm", "fore"]];
const SIDES = ["l", "r"] as const;

/** Vertices this close (model units) to a part's lowest, standing at rest, are its sole (foot_plant.py SOLE). */
const SOLE_BAND = 0.0015;

/**
 * Where a leg part's sole actually is, in the part's own space: the middle of its vertices that stand lowest in the
 * rest pose (the body's space, the road's up), the point the walk clips plant (art/unitkit/foot_plant.py `_sole_local`;
 * the part's own lowest, read in its own space, was another point of a sprawled or angled foot, and a pin on it let
 * the planted point turn across the road). The rigid rig keeps each part's vertices in the part's space under a bone
 * that is the part's child (rigidRig.ts). Null when the part draws nothing of its own. Read once per view, at attach.
 */
function drawnSole(body: THREE.Object3D, part: THREE.Object3D): THREE.Vector3 | null {
  let minY = Infinity;
  const pts: number[] = [];
  const v = new THREE.Vector3(), m = new THREE.Matrix4(), up = new THREE.Matrix4();
  for (let o: THREE.Object3D | null = part; o && o !== body; o = o.parent) { o.updateMatrix(); up.premultiply(o.matrix); }
  body.traverse((o) => {
    const skin = o as THREE.SkinnedMesh;
    if (!skin.isSkinnedMesh || !skin.skeleton) return;
    const pos = skin.geometry.getAttribute("position"), idx = skin.geometry.getAttribute("skinIndex");
    if (!pos || !idx) return;
    skin.skeleton.bones.forEach((bone, b) => {
      // the part's own vertices hang under bones below it (directly, or through a child node such as a hoof)
      m.identity();
      let o: THREE.Object3D | null = bone;
      while (o && o !== part) { o.updateMatrix(); m.premultiply(o.matrix); o = o.parent; }
      if (o !== part) return;
      for (let i = 0; i < idx.count; i++) {
        if (idx.getX(i) !== b) continue;
        v.fromBufferAttribute(pos, i).applyMatrix4(m);
        pts.push(v.x, v.y, v.z);
        minY = Math.min(minY, v.clone().applyMatrix4(up).y);
      }
    });
  });
  if (pts.length === 0) return null;
  const c = new THREE.Vector3();
  let n = 0;
  for (let i = 0; i < pts.length; i += 3) {
    v.set(pts[i], pts[i + 1], pts[i + 2]);
    if (v.applyMatrix4(up).y <= minY + SOLE_BAND) { c.x += pts[i]; c.y += pts[i + 1]; c.z += pts[i + 2]; n++; }
  }
  return c.multiplyScalar(1 / n);
}
